import type { BackgroundSolver } from '@puzzlemania/blockjam';

/**
 * Module 83 (WP-83.5b) — the app's off-thread route solver for Block Jam.
 *
 * Why: the stored route is searched at generation, and an 11-block Expert
 * board costs 1.6–6 s in Chromium (5 s reported on the web build, 6–24 s on a
 * throttled phone). On the main thread that is a frozen game; here it is a
 * Web Worker running the package's own `shortSolve` (`solver.worker.ts`), so
 * the thread stays free and the next puzzle's route is ready before the
 * player reaches it. The ramp puzzles never get here — their routes ship in
 * `SOLUTION_TABLE` — so this serves the endless phase and the Daily.
 *
 * Returns `undefined` where workers do not exist (jsdom, Node), and the game
 * then searches on the main thread exactly as before. A worker that fails
 * rejects every request in flight, which the plugin treats the same way, and
 * the next request starts a fresh worker.
 */
type Moves = Awaited<ReturnType<BackgroundSolver>>;

export function createBackgroundSolver(): BackgroundSolver | undefined {
  if (typeof Worker === 'undefined') return undefined;
  let worker: Worker | null = null;
  let nextId = 1;
  const pending = new Map<
    number,
    { resolve: (moves: Moves) => void; reject: (e: unknown) => void }
  >();
  const failAll = (error: unknown): void => {
    for (const p of pending.values()) p.reject(error);
    pending.clear();
    worker?.terminate();
    worker = null;
  };
  const ensure = (): Worker => {
    if (worker) return worker;
    const w = new Worker(new URL('./solver.worker.ts', import.meta.url), { type: 'module' });
    w.onmessage = (event: MessageEvent<{ id: number; moves: Moves }>) => {
      const p = pending.get(event.data.id);
      if (!p) return;
      pending.delete(event.data.id);
      p.resolve(event.data.moves);
    };
    w.onerror = (event) => failAll(event.error ?? new Error(event.message));
    worker = w;
    return w;
  };
  return (request) =>
    new Promise<Moves>((resolve, reject) => {
      const id = nextId++;
      pending.set(id, { resolve, reject });
      try {
        ensure().postMessage({ id, request });
      } catch (error) {
        pending.delete(id);
        reject(error);
      }
    });
}
